import { useEffect, useMemo, useRef, useSyncExternalStore, type RefObject } from 'react'
import type { DocumentReference } from './chat-pane.types'

export type ActiveDocumentSnapshot = {
  id: string | undefined
  title: string | undefined
  path: string | null
}

export type ActiveDocumentContextStore = {
  activeDocumentIdRef: RefObject<string | undefined>
  activeDocumentTitleRef: RefObject<string | undefined>
  getSnapshot: () => ActiveDocumentSnapshot
  subscribe: (listener: () => void) => () => void
}

function resolveActiveDocumentPath(
  activeDocumentId: string | undefined,
  references: DocumentReference[],
) {
  if (!activeDocumentId || references.length === 0) return null
  const ref = references.find((item) => item.id === activeDocumentId)
  return ref?.path ?? null
}

export function useActiveDocumentContextStore(
  activeDocumentId: string | undefined,
  activeDocumentTitle: string | undefined,
  references: DocumentReference[],
) {
  const activeDocumentIdRef = useRef<string | undefined>(activeDocumentId)
  const activeDocumentTitleRef = useRef<string | undefined>(activeDocumentTitle)
  const listenersRef = useRef(new Set<() => void>())
  const snapshotRef = useRef<ActiveDocumentSnapshot>({
    id: activeDocumentId,
    title: activeDocumentTitle,
    path: resolveActiveDocumentPath(activeDocumentId, references),
  })

  const store = useMemo<ActiveDocumentContextStore>(
    () => ({
      activeDocumentIdRef,
      activeDocumentTitleRef,
      getSnapshot: () => snapshotRef.current,
      subscribe: (listener) => {
        listenersRef.current.add(listener)
        return () => listenersRef.current.delete(listener)
      },
    }),
    [],
  )

  useEffect(() => {
    activeDocumentIdRef.current = activeDocumentId
    activeDocumentTitleRef.current = activeDocumentTitle

    const nextSnapshot = {
      id: activeDocumentId,
      title: activeDocumentTitle,
      path: resolveActiveDocumentPath(activeDocumentId, references),
    }
    const prevSnapshot = snapshotRef.current
    if (
      prevSnapshot.id === nextSnapshot.id &&
      prevSnapshot.title === nextSnapshot.title &&
      prevSnapshot.path === nextSnapshot.path
    ) {
      return
    }

    snapshotRef.current = nextSnapshot
    listenersRef.current.forEach((listener) => listener())
  }, [activeDocumentId, activeDocumentTitle, references])

  return store
}

export function useActiveDocumentSnapshot(store: ActiveDocumentContextStore) {
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot)
}
